import { stringSimilarity } from "../utils/stringSimilarity";
import { HAZARD_CLASS } from "./stripDotShippingDetails";

/**
 * The parts of a DOT basic description (49 CFR 172.202) that a manifest
 * comparison distinguishes. The first four must match exactly; the shipping
 * name (proper shipping name plus technical names) is compared loosely.
 * `idNumberMissing` is an eManifest UN/NA number the other side leaves out.
 */
export type DotDescriptionPart =
  | "idNumber"
  | "idNumberMissing"
  | "hazardClass"
  | "packingGroup"
  | "reportableQuantity"
  | "shippingName";

const DOT_DESCRIPTION_PARTS: readonly DotDescriptionPart[] = [
  "idNumber",
  "idNumberMissing",
  "hazardClass",
  "packingGroup",
  "reportableQuantity",
  "shippingName",
];

export const DOT_DESCRIPTION_PART_LABELS: Record<DotDescriptionPart, string> = {
  idNumber: "UN/NA number",
  idNumberMissing: "UN/NA number",
  hazardClass: "hazard class",
  packingGroup: "packing group",
  reportableQuantity: "RQ",
  shippingName: "shipping name",
};

export type DotDescription = {
  /** "UN1993" / "NA3082", spaces removed. */
  idNumber: string | null;
  /** "3", "6.1", "1.4G". Subsidiary classes are not read. */
  hazardClass: string | null;
  packingGroup: "I" | "II" | "III" | null;
  reportableQuantity: boolean;
  /** Sorted words of the proper shipping name and technical names. */
  shippingNameWords: string[];
};

const ID_NUMBER = /\b(UN|NA)\s*-?\s*(\d{4})\b/;
const EXPLICIT_PACKING_GROUP = /\bPG\s*-?\s*(III|II|I|[123])\b/g;
const ERG_REFERENCE = /\bERG\s*#?\s*\d+/g;
const PARENTHETICAL = /\([^)]*\)|\[[^\]]*\]/g;
// Class 1 divisions carry a compatibility group letter (1.4G, 1.4S).
const HAZARD_CLASS_IN_TEXT = new RegExp(
  `(?<=^|[\\s,;])(1\\.[1-6][A-HJKLNS]?|${HAZARD_CLASS.source})(?=$|[\\s,;.])`
);
const PACKING_GROUP_BY_TOKEN: Record<string, DotDescription["packingGroup"]> = {
  I: "I",
  II: "II",
  III: "III",
  "1": "I",
  "2": "II",
  "3": "III",
};
/** Words that are description structure or shipping-paper notation, not the name. */
const NON_NAME_WORDS = new Set(["RQ", "PG", "UN", "NA", "ERG", "LTD", "QTY", "II", "III", "AND"]);
const SHIPPING_NAME_SIMILARITY = 0.9;

/** Same-length blanking keeps match indexes valid against the original text. */
function blank(text: string, pattern: RegExp): string {
  return text.replace(pattern, (match) => " ".repeat(match.length));
}

function words(text: string): string[] {
  return text
    .replace(/\bN\.?\s*O\.?\s*S\b\.?/g, " NOS ")
    .replace(/\bN\.?\s*O\.?\s*I\b\.?/g, " NOI ")
    .split(/[^A-Z0-9]+/)
    .filter((word) => /^[A-Z]{2,}$/.test(word) && !NON_NAME_WORDS.has(word));
}

/**
 * "PG II", "PGII", "PG-II", "PG 2", or a bare roman numeral. A bare numeral
 * counts only after the hazard class ("9, III, RQ", "8 II"), where the
 * regulation puts the packing group, so a stray "I" in the name isn't read as
 * one. Without a class, only a numeral standing as its own comma-separated
 * entry counts.
 */
function packingGroupFrom(text: string, classEnd: number | null): DotDescription["packingGroup"] {
  const explicit = [...text.matchAll(EXPLICIT_PACKING_GROUP)][0]?.[1];
  if (explicit) return PACKING_GROUP_BY_TOKEN[explicit] ?? null;
  const candidates =
    classEnd == null
      ? text.split(",").map((segment) => segment.trim())
      : text.slice(classEnd).split(/[\s,;]+/);
  const bare = candidates.find((token) => /^I{1,3}$/.test(token));
  return bare ? (PACKING_GROUP_BY_TOKEN[bare] ?? null) : null;
}

/**
 * The shipping name is what precedes the hazard class, plus any parenthesized
 * technical names written after it. Everything else after the class (ERG
 * number, approval and profile codes, waste codes) is shipping-paper notation.
 * Words containing digits are dropped: they are codes, not name.
 */
function shippingNameWords(upper: string, classIndex: number | null): string[] {
  const notation = blank(
    blank(blank(upper, new RegExp(ID_NUMBER.source, "g")), ERG_REFERENCE),
    EXPLICIT_PACKING_GROUP
  );
  if (classIndex == null) return words(notation).sort();
  const beforeClass = notation.slice(0, classIndex);
  const technicalNamesAfter = (notation.slice(classIndex).match(PARENTHETICAL) ?? []).join(" ");
  return [...words(beforeClass), ...words(technicalNamesAfter)].sort();
}

/**
 * Picks the parts out of a DOT description wherever they sit, since real
 * shipping papers move RQ to the end, write the packing group as "PGII",
 * "PG II" or a bare "II", and trail ERG numbers and approval codes. Returns
 * null when neither an identification number nor a hazard class is found —
 * the text isn't a recognizable DOT description.
 */
export function parseDotDescription(text: string | null | undefined): DotDescription | null {
  if (!text?.trim()) return null;
  const upper = text.toUpperCase();

  const id = ID_NUMBER.exec(upper);
  const masked = blank(
    blank(blank(blank(upper, PARENTHETICAL), new RegExp(ID_NUMBER.source, "g")), ERG_REFERENCE),
    EXPLICIT_PACKING_GROUP
  );
  const hazardClass = HAZARD_CLASS_IN_TEXT.exec(masked);
  if (!id && !hazardClass) return null;

  return {
    idNumber: id ? `${id[1]}${id[2]}` : null,
    hazardClass: hazardClass?.[1] ?? null,
    packingGroup: packingGroupFrom(
      blank(upper, PARENTHETICAL),
      hazardClass ? hazardClass.index + hazardClass[0].length : null
    ),
    reportableQuantity: /\bRQ\b/.test(upper),
    shippingNameWords: shippingNameWords(upper, hazardClass?.index ?? null),
  };
}

/**
 * An exact part differs when both sides carry it with different values, or
 * the eManifest lacks it and the other side has it. A part only the eManifest
 * carries is missing data on the other side, not a disagreement — the same
 * rule the whole-field comparisons follow. The UN/NA number is the exception:
 * dotDescriptionDifferences names its absence as `idNumberMissing`.
 */
function exactPartDiffers(eValue: string | null, otherValue: string | null): boolean {
  if (otherValue == null) return false;
  return eValue !== otherValue;
}

function shippingNamesMatch(a: string[], b: string[]): boolean {
  if (a.length === 0 || b.length === 0) return true;
  return stringSimilarity(a.join(" "), b.join(" ")) >= SHIPPING_NAME_SIMILARITY;
}

/**
 * The parts on which two DOT descriptions disagree, eManifest first. Null when
 * either side isn't a recognizable DOT description, so the caller can fall
 * back to comparing the text as a whole.
 */
export function dotDescriptionDifferences(
  eManifestText: string | null | undefined,
  otherText: string | null | undefined
): DotDescriptionPart[] | null {
  const e = parseDotDescription(eManifestText);
  const other = parseDotDescription(otherText);
  if (!e || !other) return null;

  const parts: DotDescriptionPart[] = [];
  if (exactPartDiffers(e.idNumber, other.idNumber)) parts.push("idNumber");
  if (e.idNumber && !other.idNumber) parts.push("idNumberMissing");
  if (exactPartDiffers(e.hazardClass, other.hazardClass)) parts.push("hazardClass");
  if (exactPartDiffers(e.packingGroup, other.packingGroup)) parts.push("packingGroup");
  if (other.reportableQuantity && !e.reportableQuantity) parts.push("reportableQuantity");
  if (!shippingNamesMatch(e.shippingNameWords, other.shippingNameWords)) {
    parts.push("shippingName");
  }
  return parts;
}

/**
 * "packing group, UN/NA number": the parts any of `otherTexts` differs from
 * the eManifest description on, in part order. Null when no part can be named
 * — the descriptions agree, or one isn't recognizable.
 */
export function describeDotDifferences(
  eManifestText: string | null | undefined,
  otherTexts: readonly (string | null | undefined)[]
): string | null {
  const differing = new Set<DotDescriptionPart>();
  for (const other of otherTexts) {
    for (const part of dotDescriptionDifferences(eManifestText, other) ?? []) differing.add(part);
  }
  const labels = new Set(
    DOT_DESCRIPTION_PARTS.filter((part) => differing.has(part)).map(
      (part) => DOT_DESCRIPTION_PART_LABELS[part]
    )
  );
  return labels.size > 0 ? [...labels].join(", ") : null;
}
